import { useState, useEffect, useCallback, useRef } from "react";

import { Button } from "@app/components/shared/Button";

import { Skeleton } from "@app/components/shared/Skeleton";

import { logger } from "@app/services/logger";

import { updateSettings } from "@app/services/adminSettingsApi";

import * as lgbApi from "@app/services/lightgbmAdminApi";

import type {

  LightGBMOverview,

  LightGBMDecisionItem,

  TrainingRun,

} from "@app/services/lightgbmAdminApi";


type SubTab = "overview" | "activity" | "training";


const SUB_TABS: Array<{ id: SubTab; label: string }> = [

  { id: "overview", label: "Overview" },

  { id: "activity", label: "Live Activity" },

  { id: "training", label: "Training" },

];


function getScoreColor(score: number | null): string {

  if (score === null || score < 0) return "#6b7280";

  if (score >= 0.8) return "#22c55e";

  if (score >= 0.6) return "#eab308";

  return "#ef4444";

}


function getPassBadge(passed: boolean | null): { label: string; color: string } {

  if (passed === null) return { label: "SKIP", color: "#6b7280" };

  if (passed) return { label: "PASS", color: "#22c55e" };

  return { label: "REJECT", color: "#ef4444" };

}


function getStatusColor(status: string): string {

  switch (status) {

    case "completed": return "#22c55e";

    case "running":

    case "pending": return "#eab308";

    case "failed": return "#ef4444";

    default: return "#6b7280";

  }

}


function fmtDate(val: string | null | undefined): string {

  if (!val) return "-";

  try { return new Date(val).toLocaleString("en-US"); } catch { return "-"; }

}


const styles: Record<string, React.CSSProperties> = {

  container: { padding: "24px", maxWidth: "1400px", margin: "0 auto" },

  header: {

    display: "flex", alignItems: "center", justifyContent: "space-between",

    marginBottom: "24px", flexWrap: "wrap", gap: "16px",

  },

  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },

  cardsRow: {

    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",

    gap: "16px", marginBottom: "24px",

  },

  statCard: {

    padding: "16px", borderRadius: "8px",

    background: "rgba(30, 41, 59, 0.8)",

    border: "1px solid rgba(148, 163, 184, 0.15)",

  },

  statValue: { fontSize: "1.5rem", fontWeight: 700, marginTop: "8px" },

  statLabel: { fontSize: "0.8rem", color: "rgba(203, 213, 225, 0.7)", fontWeight: 500 },

  subTabsNav: {

    display: "flex", gap: "4px", marginBottom: "24px",

    borderBottom: "1px solid rgba(148, 163, 184, 0.15)", overflowX: "auto" as const,

  },

  subTabButton: {

    padding: "10px 20px", border: "none", background: "transparent",

    color: "rgba(203, 213, 225, 0.7)", cursor: "pointer", fontSize: "0.9rem",

    fontWeight: 500, borderBottom: "2px solid transparent", transition: "all 0.2s ease",

    whiteSpace: "nowrap" as const,

  },

  sectionTitle: { fontSize: "1.1rem", fontWeight: 600, marginBottom: "16px", color: "#f1f5f9" },

  table: { width: "100%", borderCollapse: "collapse" as const, fontSize: "0.85rem" },

  th: {

    padding: "10px 12px", textAlign: "left" as const,

    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",

    color: "rgba(203, 213, 225, 0.7)", fontWeight: 600, fontSize: "0.8rem",

    textTransform: "uppercase" as const, letterSpacing: "0.3px", whiteSpace: "nowrap" as const,

  },

  td: {

    padding: "10px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.08)",

    verticalAlign: "middle" as const, fontSize: "0.85rem",

  },

  emptyState: { padding: "40px 20px", textAlign: "center" as const, color: "rgba(203, 213, 225, 0.5)" },

  errorState: {

    padding: "24px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)",

    border: "1px solid rgba(239, 68, 68, 0.3)", textAlign: "center" as const, marginBottom: "16px",

  },

  badge: {

    display: "inline-block", padding: "2px 8px", borderRadius: "4px",

    fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase" as const,

  },

};


function StatusBadge({ status }: { status: string }) {

  return (

    <span style={{ ...styles.badge, color: getStatusColor(status), background: `${getStatusColor(status)}22` }}>

      {status}

    </span>

  );

}


export function LightGBMControlCenterTab() {

  const [activeSubTab, setActiveSubTab] = useState<SubTab>("overview");


  return (

    <div style={styles.container}>

      <style>{`

        @keyframes lgbPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

      `}</style>

      <div style={styles.header}>

        <div style={styles.headerLeft}>

          <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1.4rem", color: "#f1f5f9" }}>

            LightGBM Control Center

          </h2>

          <span style={{ ...styles.badge, background: "rgba(168, 85, 247, 0.15)", color: "#a855f7" }}>

            LGB

          </span>

        </div>

      </div>


      <div style={styles.subTabsNav}>

        {SUB_TABS.map((tab) => (

          <button

            key={tab.id}

            style={{

              ...styles.subTabButton,

              color: activeSubTab === tab.id ? "#f1f5f9" : "rgba(203, 213, 225, 0.7)",

              borderBottomColor: activeSubTab === tab.id ? "#a855f7" : "transparent",

            }}

            onClick={() => setActiveSubTab(tab.id)}

          >

            {tab.label}

          </button>

        ))}

      </div>


      {activeSubTab === "overview" && <OverviewPanel />}

      {activeSubTab === "activity" && <ActivityPanel />}

      {activeSubTab === "training" && <TrainingPanel />}

    </div>

  );

}


/* ── Overview Panel ── */


function OverviewPanel() {

  const [overview, setOverview] = useState<LightGBMOverview | null>(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [toggling, setToggling] = useState(false);


  const load = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const data = await lgbApi.fetchOverview();

      setOverview(data);

    } catch (err: any) {

      const msg = err?.response?.data?.detail || err?.message || "Failed to load overview";

      setError(msg);

    } finally {

      setLoading(false);

    }

  }, []);


  useEffect(() => { load(); }, [load]);

  useEffect(() => {

    const iv = setInterval(load, 30000);

    return () => clearInterval(iv);

  }, [load]);


  const handleToggle = useCallback(async (enabled: boolean) => {

    setToggling(true);

    try {

      await updateSettings({

        updates: [{ key: "gate.lgb_enabled", value: enabled }],

      });

      if (overview) {

        setOverview({ ...overview, enabled });

      }

    } catch (err: any) {

      const msg = err?.response?.data?.detail || err?.message || "Failed to toggle LightGBM";

      setError(msg);

      logger.error("LightGBM toggle error", err instanceof Error ? err : new Error(String(err)));

    } finally {

      setToggling(false);

    }

  }, [overview]);


  if (loading && !overview) {

    return (

      <div style={styles.cardsRow}>

        {[1, 2, 3, 4].map((i) => (

          <div key={i} style={styles.statCard}>

            <Skeleton width="60%" height="16px" />

            <Skeleton width="40%" height="24px" style={{ marginTop: "8px" }} />

          </div>

        ))}

      </div>

    );

  }


  if (error) {

    return (

      <div style={styles.errorState}>

        <p>{error}</p>

        <Button onClick={load} style={{ marginTop: "8px" }}>Retry</Button>

      </div>

    );

  }


  if (!overview) return null;


  const enabledColor = overview.enabled ? "#22c55e" : "#ef4444";

  const healthColor = overview.gateHealthy ? "#22c55e" : "#ef4444";


  return (

    <div>

      <div style={styles.cardsRow}>

        <div style={{ ...styles.statCard, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>

          <div style={styles.statLabel}>Status</div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "4px" }}>

            <div style={{ ...styles.statValue, color: enabledColor, fontSize: "1.2rem" }}>

              {overview.enabled ? "ENABLED" : "DISABLED"}

            </div>

            <label style={{

              position: "relative", display: "inline-block", width: "44px", height: "24px",

              cursor: toggling ? "wait" : "pointer", opacity: toggling ? 0.6 : 1,

            }}>

              <input

                type="checkbox"

                checked={overview.enabled}

                onChange={(e) => handleToggle(e.target.checked)}

                disabled={toggling}

                style={{ opacity: 0, width: 0, height: 0, position: "absolute" }}

              />

              <span style={{

                position: "absolute", top: 0, left: 0, right: 0, bottom: 0,

                backgroundColor: overview.enabled ? "#22c55e" : "rgba(148,163,184,0.25)",

                borderRadius: "24px", transition: "background-color 0.25s",

              }} />

              <span style={{

                position: "absolute", left: overview.enabled ? "22px" : "2px", top: "2px",

                width: "20px", height: "20px", borderRadius: "50%",

                backgroundColor: "#fff", transition: "left 0.25s",

              }} />

            </label>

          </div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Threshold</div>

          <div style={styles.statValue}>{overview.threshold.toFixed(2)}</div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Gate Health</div>

          <div style={{ ...styles.statValue, color: healthColor }}>

            {overview.gateHealthy ? "HEALTHY" : "DEGRADED"}

          </div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Pass Rate (24h)</div>

          <div style={styles.statValue}>

            {overview.passRate24h !== null ? `${overview.passRate24h}%` : "N/A"}

          </div>

        </div>

      </div>


      <div style={{ ...styles.cardsRow, gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Decisions (24h)</div>

          <div style={styles.statValue}>{(overview.decisions24h ?? 0).toLocaleString()}</div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Total Decisions</div>

          <div style={styles.statValue}>{(overview.totalDecisions ?? 0).toLocaleString()}</div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Total Samples</div>

          <div style={styles.statValue}>{(overview.totalSamples ?? 0).toLocaleString()}</div>

        </div>

        <div style={styles.statCard}>

          <div style={styles.statLabel}>Mode</div>

          <div style={styles.statValue}>{overview.mode}</div>

        </div>

        {overview.modelVersion && (

          <div style={styles.statCard}>

            <div style={styles.statLabel}>Model Version</div>

            <div style={{ ...styles.statValue, fontSize: "1rem" }}>{overview.modelVersion}</div>

          </div>

        )}

        {overview.trainedAt && (

          <div style={styles.statCard}>

            <div style={styles.statLabel}>Trained At</div>

            <div style={{ ...styles.statValue, fontSize: "0.9rem" }}>{fmtDate(overview.trainedAt)}</div>

          </div>

        )}

      </div>

    </div>

  );

}


/* ── Activity Panel ── */


function ActivityPanel() {

  const [decisions, setDecisions] = useState<LightGBMDecisionItem[]>([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [total, setTotal] = useState(0);

  const [totalPages, setTotalPages] = useState(1);

  const pageSize = 50;


  const load = useCallback(async (p: number) => {

    setLoading(true);

    try {

      const data = await lgbApi.fetchDecisions({ page: p, pageSize });

      setDecisions(data.items);

      setTotal(data.total);

      setTotalPages(data.totalPages);

      setPage(p);

    } catch (err: any) {

      logger.error("LightGBM decisions fetch error", err);

    } finally {

      setLoading(false);

    }

  }, []);


  useEffect(() => { load(1); }, [load]);


  if (decisions.length === 0 && !loading) {

    return (

      <div style={styles.emptyState}>

        <p>No LightGBM decisions found</p>

        <p style={{ fontSize: "0.85rem", marginTop: "8px" }}>

          Decisions will appear here once LightGBM is enabled and models are trained.

        </p>

      </div>

    );

  }


  return (

    <div style={{ overflowX: "auto" }}>

      <table style={styles.table}>

        <thead>

          <tr>

            <th style={styles.th}>Time</th>

            <th style={styles.th}>Symbol</th>

            <th style={styles.th}>Score</th>

            <th style={styles.th}>Threshold</th>

            <th style={styles.th}>Result</th>

            <th style={styles.th}>Reason</th>

          </tr>

        </thead>

        <tbody>

          {decisions.map((d) => {

            const badge = getPassBadge(d.passed);

            return (

              <tr key={d.id}>

                <td style={styles.td}>{d.createdAt ? new Date(d.createdAt).toLocaleString() : "N/A"}</td>

                <td style={styles.td}><strong>{d.symbol}</strong></td>

                <td style={styles.td}>

                  <span style={{ color: getScoreColor(d.score), fontWeight: 600 }}>

                    {d.score != null ? d.score.toFixed(4) : "-"}

                  </span>

                </td>

                <td style={styles.td}>{d.threshold != null ? d.threshold.toFixed(2) : "-"}</td>

                <td style={styles.td}>

                  <span style={{ ...styles.badge, background: badge.color + "20", color: badge.color }}>

                    {badge.label}

                  </span>

                </td>

                <td style={{ ...styles.td, maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>

                  {d.reason || "-"}

                </td>

              </tr>

            );

          })}

        </tbody>

      </table>


      {totalPages > 1 && (

        <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginTop: "16px" }}>

          <Button onClick={() => load(page - 1)} disabled={page <= 1} variant="secondary">Previous</Button>

          <span style={{ padding: "8px 16px", color: "rgba(203, 213, 225, 0.7)" }}>

            Page {page} of {totalPages} ({total.toLocaleString()} total)

          </span>

          <Button onClick={() => load(page + 1)} disabled={page >= totalPages} variant="secondary">Next</Button>

        </div>

      )}

    </div>

  );

}


/* ── Training Panel ── */


function TrainingPanel() {

  const [runs, setRuns] = useState<TrainingRun[]>([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const [triggering, setTriggering] = useState(false);

  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  const [logLines, setLogLines] = useState<string[]>([]);

  const [viewingLogs, setViewingLogs] = useState(false);

  const [autoScroll, setAutoScroll] = useState(true);

  const logRef = useRef<HTMLDivElement>(null);

  const wasRunningRef = useRef(false);


  const loadRuns = useCallback(async () => {

    try {

      const data = await lgbApi.getTrainingRuns();

      setRuns(data.runs || []);

      setError(null);

    } catch (err: any) {

      const msg = err?.response?.data?.detail || err?.message || "Failed to load training runs";

      setError(msg);

      logger.error("LightGBM training runs error", err);

    } finally {

      setLoading(false);

    }

  }, []);


  useEffect(() => {

    loadRuns();

    const iv = setInterval(loadRuns, 8000);

    return () => clearInterval(iv);

  }, [loadRuns]);


  const handleTrigger = useCallback(async () => {

    setTriggering(true);

    try {

      const result = await lgbApi.triggerTraining();

      if (result.success) {

        setActiveRunId(result.id || null);

        setLogLines([]);

        setViewingLogs(true);

        setLoading(true);

      }

    } catch (err: any) {

      logger.error("Failed to trigger LightGBM training", err);

    } finally {

      setTriggering(false);

    }

  }, []);


  // Poll for training logs when viewing

  useEffect(() => {

    if (!activeRunId || !viewingLogs) return;

    let cancelled = false;

    const poll = async () => {

      try {

        const data = await lgbApi.getTrainingRuns();

        const run = (data.runs || []).find((r) => r.id === activeRunId);

        const logs = await lgbApi.getTrainingLogs(activeRunId, 0);

        if (!cancelled) {

          setLogLines(logs.lines);

          const isFinished = run && run.status !== "running" && run.status !== "pending";

          if (wasRunningRef.current && isFinished) {

            setActiveRunId(null);

            loadRuns();

          }

          if (run && (run.status === "running" || run.status === "pending")) {

            wasRunningRef.current = true;

          }

        }

      } catch {

        // ignore poll errors

      }

    };

    wasRunningRef.current = false;

    poll();

    const iv = setInterval(poll, 2500);

    return () => { cancelled = true; clearInterval(iv); };

  }, [activeRunId, viewingLogs, loadRuns]);


  useEffect(() => {

    if (autoScroll && logRef.current) {

      logRef.current.scrollTop = logRef.current.scrollHeight;

    }

  }, [logLines, autoScroll]);


  const activeRun = runs.find((r) => r.id === activeRunId);

  const isRunning = activeRun?.status === "running" || activeRun?.status === "pending";


  return (

    <div>

      <div style={{ marginBottom: "20px", display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>

        <Button

          type="button"

          variant="primary"

          size="small"

          onClick={handleTrigger}

          disabled={triggering || viewingLogs}

          style={{

            background: "rgba(168, 85, 247, 0.2)",

            border: "1px solid rgba(168, 85, 247, 0.4)",

            color: "#c084fc",

          }}

        >

          {triggering ? "Starting..." : "Train LightGBM Filter Model"}

        </Button>

        <span style={{ fontSize: "0.75rem", color: "rgba(203,213,225,0.5)" }}>

          Trains the LightGBM filter model using existing labeled samples from the database.

        </span>

      </div>


      {/* Live Log View */}

      {(viewingLogs || isRunning) && activeRunId && (

        <div style={{ marginBottom: "20px", background: "#0f172a", borderRadius: "8px", border: "1px solid rgba(148,163,184,0.2)", overflow: "hidden" }}>

          <div style={{ padding: "8px 16px", background: "rgba(30,41,59,0.9)", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(148,163,184,0.15)" }}>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

              {isRunning ? (

                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#eab308", animation: "lgbPulse 1.5s infinite" }} />

              ) : (

                <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: activeRun?.status === "completed" ? "#22c55e" : "#ef4444" }} />

              )}

              <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#f1f5f9" }}>Training Log</span>

              <span style={{ fontSize: "0.7rem", color: "rgba(203,213,225,0.4)", fontFamily: "monospace" }}>{activeRunId.slice(0, 12)}...</span>

              {activeRun && (

                <StatusBadge status={activeRun.status} />

              )}

            </div>

            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>

              <label style={{ fontSize: "0.72rem", color: "rgba(203,213,225,0.6)", cursor: "pointer", userSelect: "none" }}>

                <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} style={{ marginRight: "4px", cursor: "pointer" }} />

                Auto-scroll

              </label>

              <Button type="button" variant="ghost" size="small" onClick={() => { setViewingLogs(false); setActiveRunId(null); }}>

                Close

              </Button>

            </div>

          </div>

          <div

            ref={logRef}

            style={{

              padding: "14px 16px",

              maxHeight: "420px",

              minHeight: "120px",

              overflow: "auto",

              fontFamily: '"Cascadia Code", "Fira Code", "Consolas", "Courier New", monospace',

              fontSize: "0.76rem",

              lineHeight: 1.55,

              color: "#94a3b8",

              whiteSpace: "pre-wrap",

              wordBreak: "break-all",

              background: "#0b1120",

            }}

          >

            {logLines.length === 0 ? (

              <div style={{ color: "rgba(148,163,184,0.4)", fontStyle: "italic", padding: "20px 0", textAlign: "center" }}>

                Waiting for training to produce output...

              </div>

            ) : (

              logLines.map((line, i) => (

                <div key={i} style={{ minHeight: "1.55em" }}>

                  {line || "\u00A0"}

                </div>

              ))

            )}

          </div>

        </div>

      )}


      {/* Training History */}

      <h3 style={styles.sectionTitle}>Training History</h3>

      {loading && runs.length === 0 ? (

        <div style={{ padding: "24px" }}><Skeleton /></div>

      ) : error && runs.length === 0 ? (

        <div style={styles.errorState}>

          <p>{error}</p>

          <Button onClick={loadRuns} style={{ marginTop: "8px" }}>Retry</Button>

        </div>

      ) : (

        <div style={{ overflowX: "auto" }}>

          <table style={styles.table}>

            <thead>

              <tr>

                <th style={styles.th}>Status</th>

                <th style={styles.th}>Started</th>

                <th style={styles.th}>Completed</th>

                <th style={styles.th}>Samples</th>

                <th style={styles.th}>Version</th>

                <th style={styles.th}>Error</th>

                <th style={styles.th}>Actions</th>

              </tr>

            </thead>

            <tbody>

              {runs.length === 0 ? (

                <tr>

                  <td colSpan={7} style={styles.emptyState}>No training runs yet</td>

                </tr>

              ) : (

                runs.map((run) => (

                  <tr key={run.id}>

                    <td style={styles.td}><StatusBadge status={run.status} /></td>

                    <td style={styles.td}>{fmtDate(run.startedAt)}</td>

                    <td style={styles.td}>{fmtDate(run.completedAt)}</td>

                    <td style={styles.td}>{run.sampleCount ?? "-"}</td>

                    <td style={styles.td}>{run.candidateVersion ?? "-"}</td>

                    <td style={{ ...styles.td, color: "#ef4444", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>

                      {run.errorMessage ?? "-"}

                    </td>

                    <td style={styles.td}>

                      <div style={{ display: "flex", gap: "6px" }}>

                        <Button

                          type="button"

                          variant="ghost"

                          size="small"

                          onClick={() => {

                            setActiveRunId(run.id);

                            setLogLines([]);

                            setViewingLogs(true);

                            lgbApi.getTrainingLogs(run.id, 0).then((logs) => {

                              setLogLines(logs.lines);

                            }).catch(() => {});

                          }}

                        >

                          View Log

                        </Button>

                        <Button

                          type="button"

                          variant="ghost"

                          size="small"

                          style={{ color: "#ef4444" }}

                          onClick={async () => {

                            if (!confirm(`Delete training run ${run.id.slice(0, 12)}...?`)) return;

                            try {

                              await lgbApi.deleteTrainingRun(run.id);

                              loadRuns();

                            } catch (e) {

                              logger.error("Failed to delete LightGBM training run", e as Error);

                            }

                          }}

                        >

                          Delete

                        </Button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      )}

    </div>

  );

}


export default LightGBMControlCenterTab;

